<template>
  <div class="tracking-page">
    <!-- 顶部工具栏 -->
    <div class="toolbar">
      <el-row :gutter="12" align="middle">
        <el-col :xs="24" :sm="8" :md="6">
          <el-input v-model="searchKeyword" placeholder="搜索客户名/联系人/内容" clearable prefix-icon="Search"
            @keyup.enter="handleSearch" @clear="handleSearch" />
        </el-col>
        <el-col :xs="12" :sm="4" :md="3">
          <el-select v-model="filterMethod" placeholder="跟进方式" clearable @change="handleSearch">
            <el-option label="电话" value="phone" />
            <el-option label="邮件" value="email" />
            <el-option label="WhatsApp" value="whatsapp" />
            <el-option label="微信" value="wechat" />
            <el-option label="拜访" value="visit" />
            <el-option label="展会" value="exhibition" />
          </el-select>
        </el-col>
        <el-col :xs="12" :sm="4" :md="3">
          <el-select v-model="filterOwner" placeholder="跟进人" clearable filterable @change="handleSearch">
            <el-option v-for="u in userList" :key="u.uid" :label="u.uname" :value="u.uid" />
          </el-select>
        </el-col>
        <el-col :xs="12" :sm="6" :md="5">
          <el-date-picker v-model="dateRange" type="daterange" range-separator="至" start-placeholder="开始日期"
            end-placeholder="结束日期" value-format="YYYY-MM-DD" @change="handleSearch" style="width:100%" />
        </el-col>
        <el-col :xs="12" :sm="4" :md="3">
          <el-checkbox v-model="showOverdue" @change="handleSearch" label="仅逾期未跟进" />
        </el-col>
        <el-col :xs="24" :sm="4" :md="4" style="text-align:right">
          <el-button type="primary" icon="Plus" @click="showAddDialog">添加跟进</el-button>
        </el-col>
      </el-row>
    </div>

    <!-- 统计卡片 -->
    <div class="stat-cards">
      <el-row :gutter="12">
        <el-col :xs="12" :sm="6">
          <el-card shadow="never" class="stat-card">
            <div class="stat-value">{{ stats.total }}</div>
            <div class="stat-label">总跟进数</div>
          </el-card>
        </el-col>
        <el-col :xs="12" :sm="6">
          <el-card shadow="never" class="stat-card stat-today">
            <div class="stat-value">{{ stats.today }}</div>
            <div class="stat-label">今日跟进</div>
          </el-card>
        </el-col>
        <el-col :xs="12" :sm="6">
          <el-card shadow="never" class="stat-card stat-overdue">
            <div class="stat-value">{{ stats.overdue }}</div>
            <div class="stat-label">逾期未跟进</div>
          </el-card>
        </el-col>
        <el-col :xs="12" :sm="6">
          <el-card shadow="never" class="stat-card stat-week">
            <div class="stat-value">{{ stats.thisWeek }}</div>
            <div class="stat-label">本周跟进</div>
          </el-card>
        </el-col>
      </el-row>
    </div>

    <!-- 双视图：时间线 / 表格 -->
    <div class="view-toggle">
      <el-radio-group v-model="viewMode" size="small">
        <el-radio-button value="table">表格</el-radio-button>
        <el-radio-button value="timeline">时间线</el-radio-button>
      </el-radio-group>
    </div>

    <!-- 表格视图 -->
    <el-table v-if="viewMode === 'table'" :data="paginatedList" stripe border style="width:100%"
      @sort-change="sortChange" :default-sort="{ prop: 'contactdate', order: 'descending' }">
      <el-table-column prop="contactdate" label="跟进时间" width="160" sortable="custom">
        <template #default="{ row }">
            <span>{{ formatDateTime(row.contactdate) }}</span>
        </template>
      </el-table-column>
      <el-table-column prop="companyname" label="客户" min-width="180" sortable show-overflow-tooltip>
        <template #default="{ row }">
          <span class="company-link" @click="goToCustomer(row)">{{ row.companyname }}</span>
        </template>
      </el-table-column>
      <el-table-column prop="contactname" label="联系人" width="100" show-overflow-tooltip />
      <el-table-column prop="method" label="方式" width="90" align="center">
        <template #default="{ row }">
          <el-tag size="small" :type="getMethodTagType(row.method)">{{ getMethodText(row.method) }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="content" label="跟进内容" min-width="260" show-overflow-tooltip />
      <el-table-column prop="username" label="跟进人" width="90" />
      <el-table-column prop="nextfollowup" label="下次跟进" width="110" sortable="custom">
        <template #default="{ row }">
          <template v-if="row.nextfollowup">
            <el-tag size="small" :type="getNextDaysTagType(row.nextfollowup)">
              {{ formatDate(row.nextfollowup) }}
            </el-tag>
          </template>
          <span v-else class="text-muted">-</span>
        </template>
      </el-table-column>
      <el-table-column label="操作" width="120" fixed="right">
        <template #default="{ row }">
          <el-button type="primary" link icon="Edit" @click="editTracking(row)" title="编辑" />
          <el-popconfirm title="确定删除此记录？" @confirm="deleteTracking(row)">
            <template #reference>
              <el-button type="danger" link icon="Delete" title="删除" />
            </template>
          </el-popconfirm>
        </template>
      </el-table-column>
    </el-table>

    <!-- 时间线视图 -->
    <div v-else class="timeline-view">
      <el-timeline>
        <el-timeline-item v-for="rec in paginatedList" :key="rec.recordid"
          :timestamp="formatDateTime(rec.contactdate)" placement="top" :type="getMethodTagType(rec.method)">
          <el-card shadow="hover" class="timeline-card">
            <div class="timeline-card-header">
              <div class="timeline-card-company">
                <span class="company-link" @click="goToCustomer(rec)">{{ rec.companyname }}</span>
                <el-tag size="small" :type="getMethodTagType(rec.method)" style="margin-left:8px">
                  {{ getMethodText(rec.method) }}
                </el-tag>
              </div>
              <div class="timeline-card-actions">
                <el-button type="primary" link icon="Edit" size="small" @click="editTracking(rec)" />
              </div>
            </div>
            <div v-if="rec.contactname" class="timeline-card-contact">
              联系人: {{ rec.contactname }}
            </div>
            <p class="timeline-card-content">{{ rec.content }}</p>
            <div v-if="rec.nextfollowup" class="timeline-card-next">
              📅 下次跟进: {{ formatDate(rec.nextfollowup) }}
              <el-tag v-if="isOverdue(rec.nextfollowup)" type="danger" size="small" style="margin-left:6px">已逾期</el-tag>
            </div>
          </el-card>
        </el-timeline-item>
      </el-timeline>
      <el-empty v-if="!paginatedList.length" description="暂无跟进记录" />
    </div>

    <!-- 分页 -->
    <div class="pagination-wrap">
      <el-pagination v-model:current-page="currentPage" v-model:page-size="pageSize"
        :page-sizes="[20, 50, 100]" :total="filteredList.length" layout="total, sizes, prev, pager, next, jumper"
        background />
    </div>

    <!-- 添加/编辑跟进对话框 -->
    <el-dialog v-model="dialogVisible" :title="trackingForm.recordid ? '编辑跟进记录' : '添加跟进记录'" width="600px" :destroy-on-close="true">
      <el-form ref="trackingFormRef" :model="trackingForm" label-width="90px" size="default">
        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="客户" required>
              <el-autocomplete v-model="companySearchText" :fetch-suggestions="searchCustomers"
                placeholder="输入客户名称搜索" @select="handleCompanySelect" style="width:100%"
                value-key="companyname" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="联系人">
              <el-select v-model="trackingForm.contactid" placeholder="选择联系人" clearable style="width:100%">
                <el-option v-for="ct in contactOptions" :key="ct.contactid" :label="ct.fullname" :value="ct.contactid" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="跟进方式" required>
              <el-select v-model="trackingForm.method" placeholder="选择方式">
                <el-option label="📞 电话" value="phone" />
                <el-option label="✉️ 邮件" value="email" />
                <el-option label="💬 WhatsApp" value="whatsapp" />
                <el-option label="💬 微信" value="wechat" />
                <el-option label="🤝 拜访" value="visit" />
                <el-option label="🎪 展会" value="exhibition" />
                <el-option label="📋 其他" value="other" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="跟进日期">
              <el-date-picker v-model="trackingForm.contactdate" type="datetime" placeholder="选择日期时间"
                value-format="YYYY-MM-DD HH:mm" style="width:100%" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="跟进内容" required>
          <el-input v-model="trackingForm.content" type="textarea" :rows="5"
            placeholder="详细记录沟通内容、客户反馈、讨论要点" />
        </el-form-item>
        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="下次跟进">
              <el-date-picker v-model="trackingForm.nextfollowup" type="date" placeholder="选择日期"
                value-format="YYYY-MM-DD" style="width:100%" />
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="saveTracking" :loading="saving">保存</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
    import { post, get } from '@/api/index'
    import { ElMessage } from 'element-plus'

    export default {
        name: 'TrackingPage',
        data() {
            return {
                // 列表
                trackingList: [],
                userList: [],
                // 筛选
                searchKeyword: '',
                filterMethod: '',
                filterOwner: '',
                dateRange: null,
                showOverdue: false,
                sortProp: 'contactdate',
                sortOrder: 'descending',
                currentPage: 1,
                pageSize: 50,
                viewMode: 'table',
                // 对话框
                dialogVisible: false,
                saving: false,
                companySearchText: '',
                contactOptions: [],
                trackingForm: this.emptyForm()
            }
        },
        computed: {
            stats() {
                const now = new Date()
                const today = now.toISOString().slice(0, 10)
                const weekStart = new Date(now); weekStart.setDate(now.getDate() - now.getDay())
                const weekStartStr = weekStart.toISOString().slice(0, 10)
                return {
                    total: this.trackingList.length,
                    today: this.trackingList.filter(t => (t.nextfollowup || '').slice(0, 10) === today).length,
                    overdue: this.trackingList.filter(t => t.nextfollowup && new Date(t.nextfollowup) < now).length,
                    thisWeek: this.trackingList.filter(t => {
                        if (!t.nextfollowup) return false
                        const d = (t.nextfollowup || '').slice(0, 10)
                        return d >= weekStartStr && d <= today
                    }).length
                }
            },
            filteredList() {
                let list = [...this.trackingList]
                const kw = this.searchKeyword.toLowerCase()
                if (kw) {
                    list = list.filter(t =>
                        (t.companyname || '').toLowerCase().includes(kw) ||
                        (t.contactname || '').toLowerCase().includes(kw) ||
                        (t.content || '').toLowerCase().includes(kw)
                    )
                }
                if (this.filterMethod) list = list.filter(t => t.method === this.filterMethod)
                if (this.filterOwner) list = list.filter(t => t.ownerid === this.filterOwner)
                if (this.dateRange && this.dateRange.length === 2) {
                    const [start, end] = this.dateRange
                    list = list.filter(t => {
                        const d = (t.contactdate || '').slice(0, 10)
                        return d >= start && d <= end
                    })
                }
                if (this.showOverdue) {
                    const now = new Date()
                    list = list.filter(t => t.nextfollowup && new Date(t.nextfollowup) < now)
                }
                // 排序
                list.sort((a, b) => {
                    const va = a[this.sortProp] || ''
                    const vb = b[this.sortProp] || ''
                    const cmp = va < vb ? -1 : va > vb ? 1 : 0
                    return this.sortOrder === 'ascending' ? cmp : -cmp
                })
                return list
            },
            paginatedList() {
                const start = (this.currentPage - 1) * this.pageSize
                return this.filteredList.slice(start, start + this.pageSize)
            }
        },
        created() {
            this.fetchTrackings()
            this.fetchUsers()
        },
        methods: {
            emptyForm() {
                return {
                    companyid: '', contactid: '', method: '', content: '',
                    contactdate: new Date().toISOString().slice(0, 16).replace('T', ' '),
                    nextfollowup: '', recordid: null
                }
            },
            async fetchTrackings() {
                try {
                    // 从customersapi获取所有跟踪记录
                    const res = await post('customersapi.asp', { action: 'gettrackingrecords' })
                    this.trackingList = res.list || []
                } catch (e) {
                    console.error(e)
                    // 备用：从dashboardapi获取
                    try {
                        const res = await post('dashboardapi.asp', { action: 'recent_activities' })
                        this.trackingList = res || []
                    } catch (e2) { console.error(e2) }
                }
            },
            async fetchUsers() {
                try {
                    const res = await post('system.asp', { action: 'users' })
                    this.userList = res.users || []
                } catch (e) { console.error(e) }
            },

            handleSearch() { this.currentPage = 1 },
            sortChange({ prop, order }) { this.sortProp = prop || 'contactdate'; this.sortOrder = order || 'descending' },

            // 客户搜索
            async searchCustomers(query, cb) {
                if (query.length < 2) { cb([]); return }
                try {
                    const res = await post('customersapi.asp', { action: 'searchcustomers', keyword: query })
                    cb((res || []).map(c => ({ ...c, value: c.companyname })))
                } catch (e) { cb([]) }
            },
            async handleCompanySelect(item) {
                this.trackingForm.companyid = item.companyid
                this.companySearchText = item.companyname
                try {
                    const res = await post('customersapi.asp', { action: 'getcontactlist', companyid: item.companyid })
                    this.contactOptions = res || []
                } catch (e) { this.contactOptions = [] }
            },

            // 添加/编辑
            showAddDialog() {
                this.trackingForm = this.emptyForm()
                this.companySearchText = ''
                this.contactOptions = []
                this.dialogVisible = true
            },
            editTracking(rec) {
                this.trackingForm = { ...rec }
                this.companySearchText = rec.companyname || ''
                this.dialogVisible = true
                if (rec.companyid) {
                    this.fetchContacts(rec.companyid)
                }
            },
            async fetchContacts(companyid) {
                try {
                    const res = await post('customersapi.asp', { action: 'getcontactlist', companyid })
                    this.contactOptions = res || []
                } catch (e) { }
            },
            async saveTracking() {
                if (!this.trackingForm.companyid || !this.trackingForm.method || !this.trackingForm.content) {
                    ElMessage.warning('请填写客户、跟进方式和内容')
                    return
                }
                this.saving = true
                try {
                    const action = this.trackingForm.recordid ? 'updatetracking' : 'addtracking'
                    const res = await post('customersapi.asp', { action, ...this.trackingForm })
                    if (res && (res.result === 'created' || res.result === 'updated' || res.recordid)) {
                        ElMessage.success('跟进记录已保存')
                        this.dialogVisible = false
                        this.fetchTrackings()
                    } else {
                        ElMessage.error(res.error || '保存失败')
                    }
                } catch (e) {
                    ElMessage.error('保存失败')
                } finally {
                    this.saving = false
                }
            },
            async deleteTracking(row) {
                try {
                    const res = await post('customersapi.asp', { action: 'deletetracking', trackingid: row.recordid })
                    if (res && res.result === 'deleted') {
                        ElMessage.success('已删除')
                        this.fetchTrackings()
                    }
                } catch (e) { ElMessage.error('删除失败') }
            },

            // 跳转
            goToCustomer(row) {
                this.$router.push({ path: '/customers', query: { id: row.companyid } })
            },

            // 工具函数
            formatDate(d) {
                if (!d) return '-'
                try { const dt = new Date(d); return isNaN(dt.getTime()) ? d : dt.toISOString().slice(0, 10) } catch { return d }
            },
            formatDateTime(d) {
                if (!d) return '-'
                try {
                    const dt = new Date(d)
                    if (isNaN(dt.getTime())) return d
                    return dt.toISOString().slice(0, 10) + ' ' + dt.toISOString().slice(11, 16)
                } catch { return d }
            },
            isOverdue(d) {
                if (!d) return false
                return new Date(d) < new Date()
            },
            getNextDaysTagType(d) {
                if (!d) return 'info'
                const diff = Math.ceil((new Date(d) - new Date()) / 86400000)
                if (diff < 0) return 'danger'
                if (diff <= 3) return 'warning'
                return 'success'
            },
            getMethodTagType(method) {
                const map = { phone: 'primary', email: 'success', whatsapp: 'success', wechat: 'success', visit: 'warning', exhibition: 'danger', other: 'info' }
                return map[method] || 'info'
            },
            getMethodText(method) {
                const map = { phone: '电话', email: '邮件', whatsapp: 'WhatsApp', wechat: '微信', visit: '拜访', exhibition: '展会', other: '其他' }
                return map[method] || method
            }
        }
    }
</script>

<style scoped>
.tracking-page { padding: 16px; }
.toolbar { margin-bottom: 12px; }
.pagination-wrap { margin-top: 12px; display: flex; justify-content: flex-end; }
.text-muted { color: var(--el-text-color-placeholder); }
.company-link { cursor: pointer; color: var(--el-color-primary); }
.company-link:hover { text-decoration: underline; }

/* 统计卡片 */
.stat-cards { margin-bottom: 16px; }
.stat-card { text-align: center; padding: 4px 0; }
.stat-value { font-size: 28px; font-weight: bold; }
.stat-label { font-size: 12px; color: var(--el-text-color-secondary); margin-top: 4px; }
.stat-today .stat-value { color: var(--el-color-primary); }
.stat-overdue .stat-value { color: var(--el-color-danger); }
.stat-week .stat-value { color: var(--el-color-success); }

/* 视图切换 */
.view-toggle { margin-bottom: 12px; }

/* 时间线视图 */
.timeline-view { max-height: 600px; overflow-y: auto; padding-right: 8px; }
.timeline-card { margin-bottom: 2px; }
.timeline-card-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px; }
.timeline-card-company { display: flex; align-items: center; }
.timeline-card-contact { font-size: 12px; color: var(--el-text-color-secondary); margin-bottom: 6px; }
.contact-info { margin-left: 10px; }
.timeline-card-content { margin: 0; font-size: 13px; white-space: pre-wrap; line-height: 1.5; }
.timeline-card-next { margin-top: 6px; font-size: 12px; color: var(--el-color-warning); }
</style>
